import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useAuth } from "@/supabase-auth";
import { useLogout } from "@/hooks/useLogout";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useProfile } from "@/hooks/useProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { ChevronLeft } from "lucide-react";
import { homeRouteFor } from "@/utils/routing";

interface AccountLayoutProps {
  children: ReactNode;
}

export function AccountLayout({ children }: AccountLayoutProps) {
  const { session, profile } = useAuth();
  const { profile: profileData } = useProfile();
  const { logout } = useLogout();
  const { primaryRole, roles } = useUserRole();

  const handleLogout = () => {
    logout("/");
  };

  // Determine back path using centralized routing
  const backPath = homeRouteFor(roles, primaryRole?.organization_id);

  const userInitials = profileData?.full_name
    ? profileData.full_name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : session?.user?.email?.[0]?.toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-header flex h-header-height items-center gap-4 border-b bg-background px-4 lg:px-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to={backPath} className="flex items-center gap-2">
            <ChevronLeft className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </Button>
        
        <div className="flex-1" />
        
        {/* User info and logout */}
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarImage src={profileData?.avatar_url} alt="Profile picture" />
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
          <span className="text-sm font-medium hidden sm:inline">
            {profileData?.full_name || session?.user?.email?.split('@')[0] || 'User'}
          </span>
          
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            Log out
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1" id="main-content" role="main" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}
